import { useRef } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { UNSAFE_noExposureExp, expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useTeamTypeShortHand } from '@atlassian/jira-atlassian-account/src/controllers/index.tsx';
import { useFetchInitialProjectTemplateName } from '@atlassian/jira-onboarding-core/src/controllers/use-fetch-initial-project-template-name/index.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import {
	KANBAN_TEAM_MANAGED_KEY,
	SCRUM_TEAM_MANAGED_KEY,
} from '@atlassian/jira-common-constants/src/project-template-type-keys.tsx';
import {
	USER_SEGMENTATION_TEAM_TYPE_MARKETING,
	USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
	USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
} from '@atlassian/jira-onboarding-core/src/constants.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge/src/utils/fire-analytics';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useFeatures } from '@atlassian/jira-navigation-apps-sidebar-next-gen-use-features/src/controllers/features/index.tsx';

export const isTailoredOnboardingM3Enabled = (): boolean => {
	// eslint-disable-next-line jira/ff/unsafe-no-exposure
	const [expConfig] = UNSAFE_noExposureExp('tailored_onboarding_m3');

	return fg('tailored_onboarding_m3_gate') && expConfig.get('isEnabled', false);
};

export const isTailoredOnboardingScrumAndSoftwareKanbanEnabled = (): boolean => {
	// eslint-disable-next-line jira/ff/unsafe-no-exposure
	const [expConfig] = UNSAFE_noExposureExp('tailored_onboarding_scrum_and_software_kanban');

	return expConfig.get('isEnabled', false);
};

/**
 * Checks if either the tailored onboarding M3 experiment or the tailored onboarding
 * scrum and software kanban experiment is enabled.
 *
 * This function is a combination check that returns true if at least one of the
 * experiments is active, allowing code to handle either case appropriately.
 *
 * @returns {boolean} True if either experiment is enabled, false otherwise.
 */
export const isTailoredOnboardingEnabled = (): boolean => {
	return isTailoredOnboardingM3Enabled() || isTailoredOnboardingScrumAndSoftwareKanbanEnabled();
};

export const isEligibleForTailoredOnboardingM3 = ({
	teamType,
	templateKey,
	isSiteAdmin,
}: {
	teamType: string | null | undefined;
	templateKey: string | undefined;
	isSiteAdmin: boolean;
}): boolean => {
	if (!isSiteAdmin) {
		return false;
	}

	const isSoftwareTeamType = teamType === USER_SEGMENTATION_TEAM_TYPE_SOFTWARE;
	const isKanbanTemplate = templateKey?.startsWith(KANBAN_TEAM_MANAGED_KEY);

	if (isKanbanTemplate && !isSoftwareTeamType && isTailoredOnboardingM3Enabled()) {
		return true;
	}

	return false;
};

export const isEligibleForTailoredOnboardingScrumAndSoftwareKanban = ({
	teamType,
	templateKey,
	isSiteAdmin,
	fireExposure = false,
	analyticEvent,
}: {
	teamType: string | null | undefined;
	templateKey: string | undefined;
	isSiteAdmin: boolean;
	fireExposure?: boolean;
	analyticEvent?: UIAnalyticsEvent;
}): boolean => {
	if (!isSiteAdmin) {
		return false;
	}

	const isSoftwareTeamType = teamType === USER_SEGMENTATION_TEAM_TYPE_SOFTWARE;
	const isScrumTemplate = templateKey?.startsWith(SCRUM_TEAM_MANAGED_KEY);
	const isKanbanTemplate = templateKey?.startsWith(KANBAN_TEAM_MANAGED_KEY);

	// Software Kanban or Scrum all Team Types
	if ((isKanbanTemplate && isSoftwareTeamType) || isScrumTemplate) {
		// User is eligible for experiment. Fires exposure for those in control too here
		if (fireExposure) {
			analyticEvent &&
				fireTrackAnalytics(
					analyticEvent,
					'tailoredOnboardingWithScrumAndSoftwareKanban personalised',
					{
						teamType,
						templateKey,
						isEnabled: expVal('tailored_onboarding_scrum_and_software_kanban', 'isEnabled', false),
					},
				);
			return expVal('tailored_onboarding_scrum_and_software_kanban', 'isEnabled', false);
		}

		if (isTailoredOnboardingScrumAndSoftwareKanbanEnabled()) {
			return true;
		}
	}

	return false;
};

export const isEligibleForTailoredOnboarding = ({
	teamType,
	templateKey,
	isSiteAdmin,
}: {
	teamType: string | null | undefined;
	templateKey: string | undefined;
	isSiteAdmin: boolean;
}): boolean => {
	return (
		isEligibleForTailoredOnboardingM3({
			teamType,
			templateKey,
			isSiteAdmin,
		}) ||
		isEligibleForTailoredOnboardingScrumAndSoftwareKanban({
			teamType,
			templateKey,
			isSiteAdmin,
		})
	);
};

// Do not use this hook for exposure, use isTailoredOnboardingEnabledWithExposure instead
export const useIsTailoredOnboardingEnabledAndEligible = () => {
	const isEnabled = isTailoredOnboardingEnabled();
	const isSiteAdmin = useIsSiteAdmin();
	const { data: projectData } = useProjectContext();
	const projectType = projectData?.projectType;
	const isSoftwareProject = projectType === 'software';
	const shouldFetch =
		isEnabled &&
		isSiteAdmin &&
		isSoftwareProject &&
		!fg('to-cleanup-remove-initial-project-template-name');

	const { isReady: teamTypeIsReady, teamType } = useTeamTypeShortHand({
		shouldFetch,
	});

	const { loading: projectContextLoading, data: projectContext } = useProjectContext();

	const { isReady: templateKeyIsReady, data: templateKey } = useFetchInitialProjectTemplateName({
		shouldFetch,
		projectId: projectContext?.projectId,
	});

	if (fg('to-cleanup-remove-initial-project-template-name')) {
		return {
			isReady: true,
			isTailoredOnboardingM3EnabledAndEligible: false,
			isTailoredOnboardingScrumAndSoftwareKanbanEnabled: false,
		};
	}

	if (!isEnabled) {
		return {
			isReady: true,
			isTailoredOnboardingM3EnabledAndEligible: false,
			isTailoredOnboardingScrumAndSoftwareKanbanEnabled: false,
		};
	}

	const isTailoredOnboardingM3EnabledAndEligible = isEligibleForTailoredOnboardingM3({
		teamType,
		templateKey,
		isSiteAdmin,
	});

	const isTailoredOnboardingEnabledWithScrumAndSoftware =
		isEligibleForTailoredOnboardingScrumAndSoftwareKanban({
			teamType,
			templateKey,
			isSiteAdmin,
		});

	return {
		isReady: teamTypeIsReady && templateKeyIsReady && !projectContextLoading,
		isTailoredOnboardingM3EnabledAndEligible: isEnabled && isTailoredOnboardingM3EnabledAndEligible,
		isTailoredOnboardingScrumAndSoftwareKanbanEnabled:
			isEnabled && isTailoredOnboardingEnabledWithScrumAndSoftware,
		teamType,
	};
};

export const isTailoredOnboardingEnabledWithExposure = ({
	isSiteAdmin,
	analyticEvent,
	teamType,
	templateKey,
}: {
	isSiteAdmin: boolean;
	analyticEvent: UIAnalyticsEvent;
	teamType: string | null | undefined;
	templateKey: string | undefined;
}): boolean => {
	if (
		isEligibleForTailoredOnboardingM3({
			teamType,
			templateKey,
			isSiteAdmin,
		})
	) {
		fireTrackAnalytics(analyticEvent, 'tailoredM3Redirect personalised', {
			teamType,
			templateKey,
			isEnabled: expVal('tailored_onboarding_m3', 'isEnabled', false),
		});

		return expVal('tailored_onboarding_m3', 'isEnabled', false);
	}

	return isEligibleForTailoredOnboardingScrumAndSoftwareKanban({
		teamType,
		templateKey,
		isSiteAdmin,
		fireExposure: true,
		analyticEvent,
	});
};

export const useIsTailoredOnboardingM2p5EligibleWithExposure = ({
	isBusinessProject,
	isTrelloCrossflow,
	isOnboarding,
}: {
	isBusinessProject: boolean;
	isTrelloCrossflow?: boolean;
	isOnboarding: boolean;
}) => {
	const isSiteAdmin = useIsSiteAdmin();
	const isBasicEligible = isSiteAdmin && isBusinessProject && !isTrelloCrossflow;
	const { isReady: teamTypeIsReady, teamType } = useTeamTypeShortHand({
		shouldFetch: isBasicEligible,
	});
	const isTargettedTeamType = ![
		USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
		USER_SEGMENTATION_TEAM_TYPE_MARKETING,
	].includes(teamType ?? '');
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireOnce = useRef(false);
	if (!isBasicEligible) {
		return { isEligible: false, isReady: true };
	}
	if (!teamTypeIsReady) {
		return { isEligible: undefined, isReady: false };
	}
	if (isBusinessProject && isSiteAdmin && isTargettedTeamType) {
		if (!fireOnce.current) {
			fireTrackAnalytics(createAnalyticsEvent({}), 'tailoredM2p5 exposed', {
				teamType,
				isOnboarding,
				isEnabled: expVal('tailored_onboarding_m2p5', 'isEnabled', false),
			});
			fireOnce.current = true;
		}
		return {
			isEligible: expVal('tailored_onboarding_m2p5', 'isEnabled', false),
			isReady: true,
			teamType,
		};
	}
	return { isEligible: false, isReady: true, teamType };
};

export const useIsScrumProject = (): boolean => {
	if (!isTailoredOnboardingScrumAndSoftwareKanbanEnabled()) {
		return false;
	}
	// eslint-disable-next-line react-hooks/react-compiler
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const { data } = useFeatures();

	return (data?.hasSprint && data?.hasBacklog) ?? false;
};
